import { type FormEvent, useEffect, useState } from 'react';
import { useShop } from '../app/boot.tsx';
import { CustomerTabs, useShopPaths } from '../app/nav.tsx';
import { fieldErrors, message } from '../lib/errors.ts';
import { Banner, Button, Dialog, Empty, Field, Page, useToast } from '../ui/index.tsx';

export function Profile() {
  const { me, api, refreshMe } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const [fullName, setFullName] = useState(me.customer?.fullName ?? '');
  const [phone, setPhone] = useState(me.customer?.phone ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registered = me.customer?.registered === true;
  useEffect(() => {
    if (!registered && !deleted) go(`/register?next=${encodeURIComponent('/profile')}`, { replace: true });
  }, [registered, deleted, go]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.updateProfile({ fullName, phone });
      await refreshMe();
      toast('บันทึกแล้ว', 'success');
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length > 0) setErrors(f);
      else toast(message(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteMe();
      setDeleted(true);
      setAskDelete(false);
      await refreshMe().catch(() => undefined);
    } catch (err) {
      setError(message(err));
    } finally {
      setDeleting(false);
    }
  }

  if (deleted) {
    return (
      <Page title="โปรไฟล์">
        <Empty title="ลบข้อมูลเรียบร้อยแล้ว" text="การจองล่วงหน้าทั้งหมดถูกยกเลิก หากต้องการใช้บริการอีกครั้ง สมัครใหม่ได้ทุกเมื่อ" />
      </Page>
    );
  }

  return (
    <>
      <Page title="โปรไฟล์">
        {error && <Banner kind="error">{error}</Banner>}
        <form onSubmit={save} noValidate>
          <Field label="ชื่อ-นามสกุล" error={errors.name}>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" maxLength={60} />
          </Field>
          <Field label="เบอร์โทรศัพท์" error={errors.phone}>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" maxLength={20} />
          </Field>
          <Button type="submit" block loading={saving}>บันทึก</Button>
        </form>
        <p className="section-title">ข้อมูลส่วนบุคคล</p>
        <p className="muted small" style={{ marginBottom: 12 }}>
          ลบชื่อและเบอร์โทรของคุณออกจากร้าน การจองล่วงหน้าจะถูกยกเลิกทั้งหมด
        </p>
        <Button variant="secondary" block onClick={() => setAskDelete(true)}>ลบข้อมูลของฉัน</Button>
        <Dialog open={askDelete} title="ลบข้อมูลของฉัน?" confirmLabel="ลบข้อมูลถาวร" danger loading={deleting}
          confirmDisabled={confirmWord.trim() !== 'ลบ'} onConfirm={doDelete} onCancel={() => { setAskDelete(false); setConfirmWord(''); }}>
          <p style={{ marginBottom: 12 }}>ข้อมูลจะถูกลบและกู้คืนไม่ได้ การจองล่วงหน้าทั้งหมดจะถูกยกเลิก</p>
          <Field label="พิมพ์คำว่า ลบ เพื่อยืนยัน">
            <input value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)} />
          </Field>
        </Dialog>
      </Page>
      <CustomerTabs />
    </>
  );
}
